import { useEffect, useState } from 'preact/hooks';

import { isAndroid } from 'react-device-detect';

import { GameOrientation } from '@/shared/api/game/types';

// Don't use orientationchange or screen.orientation, because they have poor/not full support
// https://caniuse.com/?search=orientationchange
// https://caniuse.com/?search=screen.orientation

type Base = 'portrait' | 'landscape';

const getInitOrientation = (base: Base): GameOrientation => {
  return window.innerHeight >= window.innerWidth && base === 'portrait'
    ? GameOrientation.PORTRAIT
    : GameOrientation.LANDSCAPE;
};

const useAndroidScreenOrientation = (b: Base = 'portrait') => {
  const [orientation, setOrientation] = useState<GameOrientation>(getInitOrientation(b));

  useEffect(() => {
    if (!screen || !screen?.orientation) return;

    const updateOrientation = (e: any) => {
      const isPortrait = !e.target.angle;

      setOrientation(
        isPortrait && b === 'portrait'
          ? GameOrientation.PORTRAIT
          : GameOrientation.LANDSCAPE
      );
    };

    screen?.orientation?.addEventListener('change', updateOrientation);
    return () => {
      screen?.orientation?.removeEventListener('change', updateOrientation);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return orientation;
};

const useBasicScreenOrientation = (b: Base = 'portrait') => {
  const orientBase = (base: Base) => window?.matchMedia(`(orientation: ${base})`);

  const [query] = useState(orientBase(b));
  const [orientation, setOrientation] = useState<GameOrientation>(getInitOrientation(b));

  useEffect(() => {
    if (!query || !query.addEventListener) return;

    const updateOrientation = (e: MediaQueryListEvent) => {
      setOrientation(
        e.matches && b === 'portrait'
          ? GameOrientation.PORTRAIT
          : GameOrientation.LANDSCAPE
      );
    };

    query?.addEventListener('change', updateOrientation);

    return () => {
      query?.removeEventListener('change', updateOrientation);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return orientation;
};

export const useScreenOrientation = isAndroid
  ? useAndroidScreenOrientation
  : useBasicScreenOrientation;
